import { useEffect, useState } from "react";
import { Copy, Check, Gift, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { fetchMyReferral } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * ReferralCard — signed-in-user block that surfaces their unique gift link,
 * a copy button, a share sheet trigger, and running totals of invites and
 * earned free months. Drop into the account/profile area OR the paywall
 * upsell so Premium users see it right after they subscribe.
 */
export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMyReferral()
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const copyLink = async () => {
    if (!data?.share_url) return;
    try {
      await navigator.clipboard.writeText(data.share_url);
      setCopied(true);
      toast.success("Link copied!");
      trackEvent("referral_copy_link", { code: data.code });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — long-press the link to copy manually.");
    }
  };

  const doNativeShare = async () => {
    if (!data?.share_url || typeof navigator.share !== "function") return copyLink();
    try {
      trackEvent("referral_share_click", { code: data.code });
      await navigator.share({
        title: "A week of TimeCapsule 24 Premium — free",
        text: `I'm gifting you ${data.trial_days} days of TimeCapsule 24. Claim yours:`,
        url: data.share_url,
      });
    } catch (err) {
      if (err?.name === "AbortError") return;
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="border-2 border-ink bg-paper-soft p-6" data-testid="referral-card-loading">
        <div className="flex items-center gap-3 font-mono uppercase tracking-widest text-[10px] text-ink-muted">
          <Loader2 size={14} className="animate-spin" /> Loading your gift link…
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="border-2 border-ink bg-paper-soft p-6 shadow-[6px_6px_0_#1A1C20]" data-testid="referral-card">
      <div className="flex items-center gap-2 font-mono uppercase tracking-[0.3em] text-[10px] text-press-red">
        <Gift size={12} /> Gift a month · earn a month
      </div>
      <h3 className="mt-2 font-headline uppercase font-black text-2xl sm:text-3xl leading-[0.95] tracking-tighter">
        Send a friend {data.trial_days} days of Premium.
      </h3>
      <p className="mt-2 font-body italic text-ink-muted text-sm">
        No card needed for them. Every friend who sticks around earns you a free month.
      </p>

      <div className="mt-5 flex items-stretch gap-2">
        <input
          readOnly
          value={data.share_url}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 bg-paper border-2 border-ink px-3 py-2.5 font-mono text-xs sm:text-sm truncate focus:outline-none"
          data-testid="referral-link-input"
        />
        <button
          onClick={copyLink}
          className="inline-flex items-center gap-1 border-2 border-ink px-3 py-2.5 bg-paper hover:bg-ink hover:text-paper font-mono uppercase tracking-widest text-[10px]"
          data-testid="referral-copy-btn"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <button
        onClick={doNativeShare}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-press-red text-paper border-2 border-press-red px-5 py-3 font-mono uppercase tracking-widest text-xs hover:bg-ink hover:border-ink"
        data-testid="referral-share-btn"
      >
        <Share2 size={14} /> Share my gift link
      </button>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <div className="border-2 border-ink px-4 py-3 bg-paper">
          <div className="font-mono uppercase tracking-widest text-[9px] text-ink-muted">Friends invited</div>
          <div className="font-headline text-3xl font-black mt-1" data-testid="referral-friends-count">
            {data.friends_invited}
          </div>
        </div>
        <div className="border-2 border-ink px-4 py-3 bg-paper">
          <div className="font-mono uppercase tracking-widest text-[9px] text-ink-muted">Months earned</div>
          <div className="font-headline text-3xl font-black mt-1" data-testid="referral-months-count">
            {data.months_earned}
          </div>
        </div>
      </div>
    </div>
  );
}
